import { FullPermissionFragment } from '../../generated/graphql.generated';
import { safeParseJSON } from '../js';

/**
 * Bounded permission-fragment matcher — deliberately NOT a general regex
 * engine. Stored permission patterns are built from segments of
 * [a-zA-Z0-9-] separated by dots, each segment possibly a `.*`/`*`
 * wildcard, with optional `|` alternatives (see AJV_PERMISSION_STRING,
 * which bounds what the assignment endpoint can store). Matching
 * segment-by-segment against the permission's dot-separated parts instead
 * of compiling the stored string as a RegExp removes the ReDoS surface: a
 * malicious stored pattern (e.g. `(.+)+Z`) could otherwise hang every
 * browser resolving it.
 */
const PERMISSION_PART_RE = /^[a-zA-Z0-9*-]+$/;
const WILDCARD_SEGMENTS = new Set(['*', '.*']);

function matchSegments(pattern: string[], patternIndex: number, parts: string[], partIndex: number): boolean {
	if (patternIndex >= pattern.length) {
		return partIndex >= parts.length;
	}
	const segment = pattern[patternIndex];
	// Only bounded segment shapes are accepted: literals or wildcards.
	const isWildcard = WILDCARD_SEGMENTS.has(segment);
	if (!isWildcard && !PERMISSION_PART_RE.test(segment)) {
		return false;
	}
	if (isWildcard) {
		// Wildcard segment consumes parts greedily (0+ for the last
		// segment, 1+ otherwise) so '.*' can span several dots.
		const isLast = patternIndex === pattern.length - 1;
		for (let take = isLast ? 0 : 1; partIndex + take <= parts.length; take++) {
			if (matchSegments(pattern, patternIndex + 1, parts, partIndex + take)) {
				return true;
			}
		}
		return false;
	}
	// Literal segment — must equal the next permission part.
	if (partIndex >= parts.length || parts[partIndex] !== segment) {
		return false;
	}
	return matchSegments(pattern, patternIndex + 1, parts, partIndex + 1);
}

function permissionFragmentMatches(stored: string, permission: string): boolean {
	// '|' alternatives are matched independently — each must fully match.
	// Legacy rows store the raw form ('users.edit'); new rows store the
	// escaped representation ('users\.edit') — accept both separators.
	for (const alternative of stored.split('|')) {
		const pattern = alternative.includes('\\.')
			? alternative.split('\\.')
			: alternative.split('.');
		if (matchSegments(pattern, 0, permission.split('.'), 0)) {
			return true;
		}
	}
	return false;
}

export function resolvePermissionFromArray(
	permissions: FullPermissionFragment[],
	permission: string,
) {
	let foundDepth = 0;
	let foundDistance = 0;
	let foundSubDistance = 0;
	let foundPermission: FullPermissionFragment;
	let exactMatch = false;

	// if it is not already in the output
	for (const p of permissions) {
		let matches = false;
		let matchDistance = 0;
		let subDistance = 0;

		if (p.permission === permission) {
			matches = true;
			exactMatch = true;
		} else {
			// Stored permissions are regex-like fragments
			// ('calendarEvents\..*', '.*\.view', '.*\.*'). regixify() only
			// escaped the FIRST dot and mangled every wildcard ('.*\.*' →
			// ^\..*\.*$, which requires a leading literal dot), so
			// wildcard-granted permissions never resolved — franchise/zone
			// admins lost gated UI like labor costs.
			//
			// Compile the fragment ONLY when it is a bounded shape
			// (segments of [a-zA-Z0-9*-] separated by \., optionally `|`-
			// separated alternatives) — otherwise reject. This keeps
			// arbitrary patterns like `(.+)+Z` (which would hang the
			// browser on backtracking) from ever being executed.
			matches = permissionFragmentMatches(p.permission, permission);
			if (matches) {
				for (const char of p.permission) {
					if (char === '*') { matchDistance++; }
				}
				subDistance = p.permission.indexOf('*');
			}
		}

		if (!matches) { continue; }

		if (
			// permission not yet found
			!foundPermission ||
			// permission depth is less than found
			foundDepth < p.zoneDepth ||
			// regex distance is less
			(foundDepth === p.zoneDepth && matchDistance < foundDistance) ||
			// *'s are further away
			(foundDepth === p.zoneDepth && matchDistance === foundDistance && foundSubDistance < subDistance)
		) {
			foundPermission = p;
			foundDepth = p.zoneDepth;
			foundDistance = matchDistance;
			foundSubDistance = subDistance;
		}

		if (exactMatch && foundPermission) {
			break;
		}
	}
	// CONSIDER: somehow combine restrictions if we are an exact match

	// permissionMap[permission] = foundPermission;
	return foundPermission;
}

export function allPermissionsExist(
	permissions: (FullPermissionFragment | undefined | boolean)[],
): boolean {
	for (const p of permissions) {
		if (!p) { return false; }
	}

	return true;
}

export function permissionHasRestrictions(
	permission: FullPermissionFragment,
	restrictionsForPermission: any,
) {
	const permissionRestrictions = safeParseJSON(permission.restrictions, {});
	for (const key of Object.keys(restrictionsForPermission)) {
		// TODO: RESTRICTIONS can be arrays, check for that
		// If the restriction value does not match the value passed to check then evaluate to false
		if (restrictionsForPermission[key] !== permissionRestrictions[key]) {
			return false;
		}
	}

	return true;
}

export function permissionHasRestriction(
	permission: FullPermissionFragment,
	name: string,
	value = true,
) {
	return permissionHasRestrictions(permission, {
		[name]: value,
	});
}
